// Preview/fallback scaffolding for the AI Reasoning Path module.
//
// This is NOT sample methodology data. It only provides the labels shown in the
// pre-run PREVIEW state (mirrors the old getPreviewStages behavior) and generic
// Ask-Upmode doorway prompts. Real runs override titles/summaries/why-matters
// from the backend `cognitiveStages` payload; these are fallbacks only.

import type { StageId } from './types';
import { STAGE_ACCENTS, STAGE_NUMBER } from './constants';

export interface PreviewStage {
  id: StageId;
  num: string;
  title: string;
  summary: string;
  accent: string;
}

// Titles/summaries carried over verbatim from the existing getPreviewStages so
// the pre-run preview reads identically to today.
export const PREVIEW_STAGES: PreviewStage[] = [
  {
    id: 'task-setup',
    num: STAGE_NUMBER['task-setup'],
    title: 'How Did AI Frame Your Product Search?',
    summary:
      'Reveals task types, confidence levels, and primary retrieval strategies across AI models.',
    accent: STAGE_ACCENTS['task-setup'],
  },
  {
    id: 'evidence-signals',
    num: STAGE_NUMBER['evidence-signals'],
    title: 'Where Did AI Look For Information & What Did It Find?',
    summary:
      'Maps domain influence, consensus levels, and extraction coverage across models.',
    accent: STAGE_ACCENTS['evidence-signals'],
  },
  {
    id: 'understanding-decisions',
    num: STAGE_NUMBER['understanding-decisions'],
    title: 'How Is AI Comparing You?',
    summary:
      'Shows your competitive position, ranking factors, and perception gaps vs alternatives.',
    accent: STAGE_ACCENTS['understanding-decisions'],
  },
  {
    id: 'final-synthesis',
    num: STAGE_NUMBER['final-synthesis'],
    title: 'What Is AI Telling People?',
    summary:
      'Reveals confidence scores, top strengths, and common weaknesses across AI responses.',
    accent: STAGE_ACCENTS['final-synthesis'],
  },
];

// Generic Ask-Upmode doorway prompts per stage (no methodology claims). Used
// when the backend stage carries no bespoke prompts.
export const STAGE_ASKS: Record<StageId, string[]> = {
  'task-setup': [
    'Why did models read this query differently?',
    'Does framing drift affect our ranking?',
  ],
  'evidence-signals': [
    'Which sources shaped the models most?',
    'How do we strengthen our owned content?',
  ],
  'understanding-decisions': [
    'What would move us up the ranking?',
    'Why do competitors outrank us here?',
  ],
  'final-synthesis': [
    'How do we standardize our narrative?',
    'Which weakness should we address first?',
  ],
};

// Generic "Why this matters" fallback copy, only used when the backend stage
// provides none.
export const STAGE_WHY_FALLBACK: Record<StageId, string> = {
  'task-setup':
    'When models read the same query differently, the rest of the reasoning diverges. Consistent framing keeps the downstream comparison fair.',
  'evidence-signals':
    'Models lean on a handful of high-influence sources. If your facts are not encoded where they look, you are invisible at the moment perception forms.',
  'understanding-decisions':
    'This is where consideration becomes ranking. A model can know you well and still place a competitor first if its evidence skews their way.',
  'final-synthesis':
    'The synthesis is what a real user sees. Inconsistent narratives across models mean your story is not landing the same way everywhere.',
};
